import React, { useState, useEffect } from 'react';

/**
 * ChartLogo Component
 * Displays the dynamic theme-based logo in the upper right corner of chart containers
 */
const ChartLogo = ({ isDarkMode, position = 'top-right' }) => {
  // Detect theme from DOM and update when it changes
  const checkLightMode = () => {
    // Check multiple ways to detect light mode
    const landingPage = document.querySelector('.landing-page');
    const hasLightModeClass = 
      (landingPage && landingPage.classList.contains('light-mode')) ||
      document.body.classList.contains('light-mode') ||
      document.documentElement.classList.contains('light-mode');
    
    return hasLightModeClass;
  };

  const [isInDarkMode, setIsInDarkMode] = useState(() => {
    if (isDarkMode !== undefined) return isDarkMode;
    // Initial check - default to dark mode if can't detect
    const isLight = checkLightMode();
    return !isLight;
  });

  useEffect(() => {
    if (isDarkMode !== undefined) {
      setIsInDarkMode(isDarkMode);
      return;
    }

    const updateTheme = () => {
      const isLightMode = checkLightMode();
      setIsInDarkMode(!isLightMode);
    };

    // Check immediately
    updateTheme();

    // Set up observer to watch for class changes
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    return () => observer.disconnect();
  }, [isDarkMode]);

  // Determine positioning styles based on position prop
  const getPositionStyles = () => {
    switch (position) {
      case 'bottom-right':
        return {
          position: 'absolute',
          bottom: '1rem',
          right: '1rem',
          zIndex: 99999
        };
      case 'top-right':
      default:
        return {
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          zIndex: 99999
        };
    }
  };

  // Use theme-appropriate icon based on dark/light mode
  const iconSrc = isInDarkMode 
    ? '/images/dark_mode_icon.png' 
    : '/images/light_mode_icon.png';
  
  return (
    <div 
      className="chart-logo" 
      style={{
        ...getPositionStyles(),
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <img 
        src={iconSrc}
        alt="Pantera Research Lab" 
        className="chart-logo-img"
        style={{ opacity: isInDarkMode ? 0.8 : 0.7 }}
      />
    </div>
  );
};

export default ChartLogo;
